/**
 * Accounting part 2 contracts (M5 task 8; 03-modules.md §12, 04-data-model.md "accounting",
 * 05-api.md "Accounting and day close", 06-flows.md F9): shifts with a counted float and
 * counted close, cash accounts and their append-only ledger, expenses with categories and
 * approval, cash in / out / transfer, the cash position, and day close.
 *
 * Money is integer paise. The balance of a cash account is the sum of its ledger rows.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema, pageQuerySchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';

// ---------------------------------------------------------------------------
// Denominations

/**
 * The notes and coins a till counts, in paise, largest first (INR: 2000, 500, 200, 100, 50,
 * 20, 10 rupee notes; 20, 10, 5, 2, 1 rupee coins). The 2000-rupee note is withdrawn but
 * still legal tender, so a drawer may hold some.
 */
export const DENOMINATIONS_PAISE = [
  200000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100,
] as const;
export type DenominationPaise = (typeof DENOMINATIONS_PAISE)[number];

export const denominationSchema = z
  .int()
  .refine((v) => (DENOMINATIONS_PAISE as readonly number[]).includes(v), {
    message: `Not a note or coin we count: ${DENOMINATIONS_PAISE.join(', ')} (paise)`,
  });

/** How many of one note or coin were counted. */
export const denominationCountSchema = z.object({
  denominationPaise: denominationSchema,
  count: z.int().min(0).max(1_000_000),
});
export type DenominationCount = z.output<typeof denominationCountSchema>;

/** A count: each denomination at most once; the total is what was counted. */
export const denominationCountsSchema = z
  .array(denominationCountSchema)
  .max(DENOMINATIONS_PAISE.length)
  .refine((rows) => new Set(rows.map((r) => r.denominationPaise)).size === rows.length, {
    message: 'Each note or coin once',
  });

/** The paise a count adds up to. */
export function totalOfCounts(counts: readonly DenominationCount[]): bigint {
  let total = 0n;
  for (const c of counts) total += BigInt(c.denominationPaise) * BigInt(c.count);
  return total;
}

// ---------------------------------------------------------------------------
// Cash accounts and the ledger

export const CASH_ACCOUNT_KINDS = ['counter', 'petty', 'bank'] as const;
export type CashAccountKind = (typeof CASH_ACCOUNT_KINDS)[number];

/** Why cash moved (04-data-model.md `cash_ledger.reason`, plus `variance`). */
export const CASH_LEDGER_REASONS = [
  'sale',
  'expense',
  'cash_in',
  'cash_out',
  'transfer',
  'opening',
  'variance',
] as const;
export type CashLedgerReason = (typeof CASH_LEDGER_REASONS)[number];

export const CASH_REF_TYPES = ['payment', 'expense', 'cash_movement', 'shift'] as const;

/** A cash account or expense category name. */
export const cashNameSchema = z.string().trim().min(1).max(80);
/** A reason or remark on a cash movement, expense or close. */
export const cashNoteSchema = z.string().trim().min(1).max(500);
const accountNameSchema = cashNameSchema;
const noteSchema = cashNoteSchema;

export const cashAccountSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  kind: z.enum(CASH_ACCOUNT_KINDS),
  name: z.string(),
  /** The till whose drawer this is (counter accounts made for a till). */
  terminalId: uuidSchema.nullable(),
  /** The sum of the account's ledger rows. */
  balancePaise: paiseSchema,
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  version: z.int(),
});
export type CashAccountView = z.output<typeof cashAccountSchema>;

/** `?archived=true` lists archived rows too. */
export const includeArchivedQuerySchema = z.object({
  archived: z.enum(['true', 'false']).optional(),
});

export const cashAccountListSchema = z.object({ data: z.array(cashAccountSchema) });

export const cashAccountParamsSchema = z.object({ outletId: uuidSchema, accountId: uuidSchema });

/** `POST {o}/cash-accounts`: a petty cash box, the bank, or an extra counter. */
export const createCashAccountSchema = z.object({
  id: uuidSchema.optional(),
  kind: z.enum(CASH_ACCOUNT_KINDS),
  name: accountNameSchema,
});

/** `PATCH {o}/cash-accounts/{id}`: rename, or archive (only at a zero balance). */
export const updateCashAccountSchema = z
  .object({ name: accountNameSchema, archived: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const cashLedgerEntrySchema = z.object({
  id: uuidSchema,
  accountId: uuidSchema,
  deltaPaise: paiseSchema,
  reason: z.enum(CASH_LEDGER_REASONS),
  shiftId: uuidSchema.nullable(),
  businessDate: businessDateSchema,
  refType: z.enum(CASH_REF_TYPES),
  refId: uuidSchema,
  note: z.string().nullable(),
  actorId: uuidSchema.nullable(),
  terminalId: uuidSchema.nullable(),
  at: isoTimestampSchema,
});
export type CashLedgerEntryView = z.output<typeof cashLedgerEntrySchema>;

/** `GET {o}/cash/ledger`: newest first; `cursor` is the `nextCursor` of the page before. */
export const cashLedgerQuerySchema = pageQuerySchema.extend({
  accountId: uuidSchema.optional(),
  shiftId: uuidSchema.optional(),
  businessDate: businessDateSchema.optional(),
  reason: z.enum(CASH_LEDGER_REASONS).optional(),
});
export const cashLedgerListSchema = z.object({
  data: z.array(cashLedgerEntrySchema),
  nextCursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Cash in, out and transfer

export const CASH_MOVEMENT_KINDS = ['in', 'out', 'transfer'] as const;

/** An amount of money that is more than zero. */
export const positivePaiseSchema = nonNegativePaiseSchema.refine((v) => v > 0n, {
  message: 'Must be more than zero',
});

/**
 * `POST {o}/cash/in` and `/cash/out`: cash put into or taken out of an account that is
 * not a sale or an expense (a float from the owner, a bank deposit, a payout). The
 * account is the calling till's drawer unless `accountId` names another; `note` says why.
 */
export const cashInOutSchema = z.object({
  id: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  amountPaise: positivePaiseSchema,
  note: noteSchema,
});

/** `POST {o}/cash/transfer`: between two accounts of the outlet (drawer to safe, safe to bank). */
export const transferCashSchema = z
  .object({
    id: uuidSchema.optional(),
    fromAccountId: uuidSchema,
    toAccountId: uuidSchema,
    amountPaise: positivePaiseSchema,
    note: noteSchema.optional(),
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'Choose two different accounts',
    path: ['toAccountId'],
  });

export const cashMovementSchema = z.object({
  id: uuidSchema,
  kind: z.enum(CASH_MOVEMENT_KINDS),
  amountPaise: paiseSchema,
  businessDate: businessDateSchema,
  entries: z.array(cashLedgerEntrySchema),
  /** The balances of the accounts it touched, after it. */
  balances: z.array(z.object({ accountId: uuidSchema, balancePaise: paiseSchema })),
});
export type CashMovementView = z.output<typeof cashMovementSchema>;

// ---------------------------------------------------------------------------
// Shifts

const shiftBase = {
  id: uuidSchema,
  outletId: uuidSchema,
  terminalId: uuidSchema,
  /** Who opened it. */
  userId: uuidSchema,
  cashAccountId: uuidSchema,
  businessDate: businessDateSchema,
  openedAt: isoTimestampSchema,
  openingCashPaise: paiseSchema,
  /** The ledger was moved by this much to meet the counted float (0 when the drawer matched). */
  openingAdjustmentPaise: paiseSchema,
  openingCounts: z.array(denominationCountSchema),
  openNote: z.string().nullable(),
  version: z.int(),
};

/** What came and went in the drawer during a shift (the spec's cash position, per shift). */
export const cashBreakdownSchema = z.object({
  /** Cash tenders net of change and voids. */
  salesPaise: paiseSchema,
  cashInPaise: paiseSchema,
  cashOutPaise: paiseSchema,
  /** Expenses paid from the drawer, net of voids. */
  expensesPaise: paiseSchema,
  transfersInPaise: paiseSchema,
  transfersOutPaise: paiseSchema,
});
export type CashBreakdown = z.output<typeof cashBreakdownSchema>;

export const shiftSchema = z.object({
  ...shiftBase,
  status: z.enum(['open', 'closed']),
  closedAt: isoTimestampSchema.nullable(),
  closedBy: uuidSchema.nullable(),
  /**
   * opening + sales + cash in − cash out − expenses ± transfers. For an open shift it is
   * the live figure; after close, what it was at close.
   */
  expectedCashPaise: paiseSchema,
  countedCashPaise: paiseSchema.nullable(),
  /** counted − expected: negative is short, positive is over. */
  variancePaise: paiseSchema.nullable(),
  closingCounts: z.array(denominationCountSchema),
  closeNote: z.string().nullable(),
  breakdown: cashBreakdownSchema,
});
export type ShiftView = z.output<typeof shiftSchema>;

export const shiftListSchema = z.object({ data: z.array(shiftSchema) });
export const shiftParamsSchema = z.object({ outletId: uuidSchema, shiftId: uuidSchema });

export const shiftListQuerySchema = z.object({
  businessDate: businessDateSchema.optional(),
  status: z.enum(['open', 'closed']).optional(),
  terminalId: uuidSchema.optional(),
});

/** `GET {o}/shifts/current`: the open shift of the calling terminal, if any. */
export const currentShiftSchema = z.object({ shift: shiftSchema.nullable() });

/**
 * `POST {o}/shifts/open`: start a shift on the calling terminal (or `terminalId`) with the
 * float counted by denomination. One open shift per terminal and per person.
 */
export const openShiftSchema = z.object({
  id: uuidSchema.optional(),
  terminalId: uuidSchema.optional(),
  counts: denominationCountsSchema,
  note: noteSchema.optional(),
});

/**
 * `POST {o}/shifts/{id}/close`: the cash counted by denomination. A count that differs
 * from what the drawer should hold needs a `note` saying why (400 on `note` otherwise).
 */
export const closeShiftSchema = z.object({
  counts: denominationCountsSchema,
  note: noteSchema.optional(),
});

// ---------------------------------------------------------------------------
// Expenses

export const EXPENSE_STATUSES = ['approved', 'pending', 'rejected', 'voided'] as const;

export const expenseCategorySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  archivedAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export type ExpenseCategoryView = z.output<typeof expenseCategorySchema>;
export const expenseCategoryListSchema = z.object({ data: z.array(expenseCategorySchema) });
export const expenseCategoryParamsSchema = z.object({
  outletId: uuidSchema,
  categoryId: uuidSchema,
});
export const createExpenseCategorySchema = z.object({
  id: uuidSchema.optional(),
  name: accountNameSchema,
});
export const updateExpenseCategorySchema = z
  .object({ name: accountNameSchema, archived: z.boolean() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const expenseSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  categoryId: uuidSchema,
  categoryName: z.string(),
  amountPaise: paiseSchema,
  paidFromAccountId: uuidSchema,
  note: z.string().nullable(),
  billFileId: uuidSchema.nullable(),
  businessDate: businessDateSchema,
  shiftId: uuidSchema.nullable(),
  /** `pending`: above the approval limit and not yet decided (the cash has gone). */
  status: z.enum(EXPENSE_STATUSES),
  recordedBy: uuidSchema.nullable(),
  decidedBy: uuidSchema.nullable(),
  decidedAt: isoTimestampSchema.nullable(),
  decisionNote: z.string().nullable(),
  voidedAt: isoTimestampSchema.nullable(),
  voidNote: z.string().nullable(),
  createdAt: isoTimestampSchema,
  version: z.int(),
});
export type ExpenseView = z.output<typeof expenseSchema>;

export const expenseParamsSchema = z.object({ outletId: uuidSchema, expenseId: uuidSchema });

/** `POST {o}/expenses`: paid from the calling till's drawer unless `paidFromAccountId` names another. */
export const recordExpenseSchema = z.object({
  id: uuidSchema.optional(),
  categoryId: uuidSchema,
  amountPaise: positivePaiseSchema,
  paidFromAccountId: uuidSchema.optional(),
  note: noteSchema.optional(),
  billFileId: uuidSchema.optional(),
});

/** `POST {o}/expenses/{id}/approve` and `/reject`. */
export const decideExpenseSchema = z.object({ note: noteSchema.optional() });
/** `POST {o}/expenses/{id}/void`: an expense recorded by mistake; the cash comes back to the account. */
export const voidExpenseSchema = z.object({ note: noteSchema });

export const expenseListQuerySchema = pageQuerySchema.extend({
  businessDate: businessDateSchema.optional(),
  status: z.enum(EXPENSE_STATUSES).optional(),
  categoryId: uuidSchema.optional(),
});
export const expenseListSchema = z.object({
  data: z.array(expenseSchema),
  nextCursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Cash position

/** `GET {o}/cash/position?businessDate=`. */
export const cashPositionQuerySchema = z.object({ businessDate: businessDateSchema.optional() });

const accountPositionSchema = cashBreakdownSchema.extend({
  accountId: uuidSchema,
  name: z.string(),
  kind: z.enum(CASH_ACCOUNT_KINDS),
  /** The balance at the start of the business date. */
  openingPaise: paiseSchema,
  /** `opening` and `variance` rows of the day: counted floats and shift variances. */
  adjustmentsPaise: paiseSchema,
  /** opening + sales + in − out − expenses ± transfers + adjustments. */
  closingPaise: paiseSchema,
});

export const cashPositionSchema = z.object({
  businessDate: businessDateSchema,
  accounts: z.array(accountPositionSchema),
  totals: cashBreakdownSchema.extend({
    openingPaise: paiseSchema,
    adjustmentsPaise: paiseSchema,
    closingPaise: paiseSchema,
  }),
  /** Cash taken at tills with no shift open (attributed to the till's drawer only). */
  salesWithoutShiftPaise: paiseSchema,
  shifts: z.array(shiftSchema),
});
export type CashPositionView = z.output<typeof cashPositionSchema>;

// ---------------------------------------------------------------------------
// Day close (06-flows.md F9 step 6)

export const DAY_BLOCKER_KINDS = [
  'open_shifts',
  'pending_expenses',
  'unsettled_orders',
  'unconfirmed_kots',
  'bar_closing',
] as const;
export type DayBlockerKind = (typeof DAY_BLOCKER_KINDS)[number];

/**
 * What keeps a day from closing. `overridable` blockers (open orders, unconfirmed KOTs)
 * can be closed over by someone with `day.close_force` (a manager's override at a till),
 * with a reason; open shifts and undecided expenses always have to be dealt with first.
 */
export const dayBlockerSchema = z.object({
  kind: z.enum(DAY_BLOCKER_KINDS),
  count: z.int(),
  overridable: z.boolean(),
});
export type DayBlocker = z.output<typeof dayBlockerSchema>;

export const openShiftCheckSchema = z.object({
  shiftId: uuidSchema,
  terminalId: uuidSchema,
  userId: uuidSchema,
  openedAt: isoTimestampSchema,
});

export const pendingExpenseCheckSchema = z.object({
  expenseId: uuidSchema,
  amountPaise: paiseSchema,
  categoryName: z.string(),
  recordedBy: uuidSchema.nullable(),
});

/** The figures frozen into the day when it is closed by hand. */
export const daySummarySchema = z.object({
  invoices: z.object({ count: z.int(), totalPaise: paiseSchema, taxPaise: paiseSchema }),
  cash: z.object({
    closingPaise: paiseSchema,
    salesPaise: paiseSchema,
    cashInPaise: paiseSchema,
    cashOutPaise: paiseSchema,
    expensesPaise: paiseSchema,
  }),
  shifts: z.object({ count: z.int(), variancePaise: paiseSchema }),
  /** Blockers closed over with `day.close_force`, and the orders still open then. */
  forced: z.boolean(),
  overriddenBlockers: z.array(dayBlockerSchema),
  openOrderIds: z.array(uuidSchema),
  reason: z.string().nullable(),
});
export type DaySummary = z.output<typeof daySummarySchema>;

/**
 * `POST {o}/days/close`: close the open business day (`businessDate` must be it). Fails with
 * 409 `DAY_CLOSE_BLOCKED` while a blocker stands; `force` with a `reason` closes over the
 * overridable ones (`day.close_force`).
 */
export const closeDaySchema = z.object({
  businessDate: businessDateSchema,
  force: z.boolean().optional(),
  reason: noteSchema.optional(),
});

// ---------------------------------------------------------------------------
// Events (03-modules.md §12 lists `shift.closed` and `expense.recorded`; the rest are new,
// see the accounting README). `day.closed` is in accounting.ts.

export const shiftOpenedV1 = defineEvent(
  'shift.opened',
  1,
  z.object({
    shiftId: uuidSchema,
    terminalId: uuidSchema,
    userId: uuidSchema,
    businessDate: businessDateSchema,
    openingCashPaise: paiseSchema,
    openingAdjustmentPaise: paiseSchema,
  }),
);

export const shiftClosedV1 = defineEvent(
  'shift.closed',
  1,
  z.object({
    shiftId: uuidSchema,
    terminalId: uuidSchema,
    userId: uuidSchema,
    businessDate: businessDateSchema,
    expectedCashPaise: paiseSchema,
    countedCashPaise: paiseSchema,
    variancePaise: paiseSchema,
  }),
);

export const cashMovedV1 = defineEvent(
  'cash.moved',
  1,
  z.object({
    movementId: uuidSchema,
    kind: z.enum(CASH_MOVEMENT_KINDS),
    amountPaise: paiseSchema,
    businessDate: businessDateSchema,
    fromAccountId: uuidSchema.nullable(),
    toAccountId: uuidSchema.nullable(),
  }),
);

export const cashAccountChangedV1 = defineEvent(
  'cash_account.changed',
  1,
  z.object({
    accountId: uuidSchema,
    kind: z.enum(CASH_ACCOUNT_KINDS),
    created: z.boolean(),
    archived: z.boolean(),
  }),
);

export const expenseCategoryChangedV1 = defineEvent(
  'expense_category.changed',
  1,
  z.object({ categoryId: uuidSchema, created: z.boolean(), archived: z.boolean() }),
);

export const expenseRecordedV1 = defineEvent(
  'expense.recorded',
  1,
  z.object({
    expenseId: uuidSchema,
    categoryId: uuidSchema,
    amountPaise: paiseSchema,
    paidFromAccountId: uuidSchema,
    businessDate: businessDateSchema,
    status: z.enum(EXPENSE_STATUSES),
  }),
);

/** An expense was approved, rejected or voided (its cash returns to the account when not approved). */
export const expenseDecidedV1 = defineEvent(
  'expense.decided',
  1,
  z.object({
    expenseId: uuidSchema,
    status: z.enum(['approved', 'rejected', 'voided']),
    amountPaise: paiseSchema,
    paidFromAccountId: uuidSchema,
    businessDate: businessDateSchema,
  }),
);

export const CASH_EVENTS = [
  shiftOpenedV1,
  shiftClosedV1,
  cashMovedV1,
  cashAccountChangedV1,
  expenseCategoryChangedV1,
  expenseRecordedV1,
  expenseDecidedV1,
] as const;
